import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  increment,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from './firebase/firebase';
import { mergeProspect } from '../utils/prospectImport';
import { scoreProspect } from '../utils/prospectScoring';

// `mergeProspect` est la règle de fusion du §3.2, pure et testée dans `prospectImport` ;
// le service la réexporte pour que les appelants n'aient qu'un seul point d'entrée.
export { mergeProspect };

/**
 * Service de prospection (côté admin) — étape 1.
 *
 * Trois responsabilités dans un seul fichier, comme `creationRequestService.js` :
 *  1. les **constantes** (statuts, issues d'appel, libellés) ;
 *  2. les **fonctions pures** (`buildCallPatch`, `computeNextCallAt`, `isCallable`, `merge`) —
 *     testables sans émulateur ;
 *  3. les **accès Firestore** (`importProspects`, `observeProspects`, `recordCall`, …).
 *
 * 🔒 `CONVERTI` est déclarée mais n'apparaît dans **aucune** valeur de `CALL_OUTCOME_EFFECTS` :
 * seul le trigger Stripe de l'étape 3 écrira cette valeur.
 */

/** Convention de clé de cache de `useCreationRequests.js` L18. */
export const PROSPECTS_KEY = 'prospects';
export const PROSPECT_IMPORTS_KEY = 'prospect_imports';

export const PROSPECT_STATUS = {
  NOUVEAU: 'nouveau',
  A_APPELER: 'a_appeler',
  A_COMPLETER: 'a_completer',
  RAPPELER: 'rappeler',
  QUALIFIE: 'qualifie',
  RDV: 'rdv',
  DEMANDE_CREEE: 'demande_creee', // étape 2
  CONVERTI: 'converti', // étape 3 — JAMAIS écrit ici
  PERDU: 'perdu',
  EXCLU: 'exclu',
  OPPOSITION: 'opposition'
};

export const PROSPECT_STATUS_LABELS = {
  [PROSPECT_STATUS.NOUVEAU]: 'Nouveau',
  [PROSPECT_STATUS.A_APPELER]: 'À appeler',
  [PROSPECT_STATUS.A_COMPLETER]: 'Adresse à compléter',
  [PROSPECT_STATUS.RAPPELER]: 'À rappeler',
  [PROSPECT_STATUS.QUALIFIE]: 'Qualifié',
  [PROSPECT_STATUS.RDV]: 'RDV planifié',
  [PROSPECT_STATUS.DEMANDE_CREEE]: 'Demande créée',
  [PROSPECT_STATUS.CONVERTI]: 'Converti (Pro)',
  [PROSPECT_STATUS.PERDU]: 'Perdu',
  [PROSPECT_STATUS.EXCLU]: 'Exclu',
  [PROSPECT_STATUS.OPPOSITION]: '⛔ Opposition'
};

/**
 * Ordre d'affichage des statuts, du plus fréquent au plus rare.
 *
 * ⚠️ Ce n'est **pas** la liste des statuts choisissables à la main : c'est `STATUTS_SELECTION`
 * (trois entrées). Depuis le 30/10/2026, `PROSPECT_STATUS_ORDER` n'a plus d'appelant dans l'étape 1
 * — il est conservé tel quel pour les filtres de l'étape 2, avec `rdv`, `demande_creee` et
 * `nouveau` que les données existantes peuvent encore porter.
 */
export const PROSPECT_STATUS_ORDER = [
  PROSPECT_STATUS.A_APPELER,
  PROSPECT_STATUS.A_COMPLETER,
  PROSPECT_STATUS.RAPPELER,
  PROSPECT_STATUS.QUALIFIE,
  PROSPECT_STATUS.RDV,
  PROSPECT_STATUS.DEMANDE_CREEE,
  PROSPECT_STATUS.PERDU,
  PROSPECT_STATUS.EXCLU,
  PROSPECT_STATUS.OPPOSITION,
  PROSPECT_STATUS.NOUVEAU
];

/** Classes daisyUI des badges de statut. */
export const PROSPECT_STATUS_BADGES = {
  [PROSPECT_STATUS.A_APPELER]: 'badge-info',
  [PROSPECT_STATUS.A_COMPLETER]: 'badge-warning',
  [PROSPECT_STATUS.RAPPELER]: 'badge-accent',
  [PROSPECT_STATUS.QUALIFIE]: 'badge-success',
  [PROSPECT_STATUS.RDV]: 'badge-success',
  [PROSPECT_STATUS.DEMANDE_CREEE]: 'badge-primary',
  [PROSPECT_STATUS.CONVERTI]: 'badge-primary',
  [PROSPECT_STATUS.PERDU]: 'badge-error',
  [PROSPECT_STATUS.EXCLU]: 'badge-ghost',
  [PROSPECT_STATUS.OPPOSITION]: 'badge-error',
  [PROSPECT_STATUS.NOUVEAU]: 'badge-ghost'
};

/**
 * Statuts terminaux : aucune issue d'appel possible, fiche en lecture seule.
 * `exclu` et `perdu` sont **réversibles** (avec confirmation) ; `opposition` ne l'est pas depuis
 * l'UI ; `converti` ne se dégrade jamais.
 */
export const TERMINAL_STATUSES = [
  PROSPECT_STATUS.CONVERTI,
  PROSPECT_STATUS.PERDU,
  PROSPECT_STATUS.EXCLU,
  PROSPECT_STATUS.OPPOSITION
];

/** Statuts dans lesquels une issue d'appel peut encore être enregistrée. */
export const STATUTS_RAPPELABLES = [
  PROSPECT_STATUS.NOUVEAU,
  PROSPECT_STATUS.A_APPELER,
  PROSPECT_STATUS.A_COMPLETER,
  PROSPECT_STATUS.RAPPELER,
  PROSPECT_STATUS.QUALIFIE,
  PROSPECT_STATUS.DEMANDE_CREEE
];

/**
 * Statuts qu'un opérateur peut poser **à la main** depuis la fiche (select « Changer le statut »).
 *
 * Trois seulement, parce que `updateProspectStatus` refuse tout statut terminal : une sortie
 * (`perdu`, `exclu`, `opposition`) **s'obtient par une issue d'appel**, jamais par un select.
 * Exclus également :
 *  - `nouveau`       — jamais écrit : l'import pose `a_appeler` ou `a_completer` ;
 *  - `a_completer`   — l'adresse ne se complète plus à l'étape 1 (elle l'est à l'étape 2) ;
 *  - `rdv`           — l'issue « RDV pris » a été retirée (redondante avec « À rappeler ») ;
 *  - `demande_creee` — réservé à l'étape 2 ;
 *  - `converti`      — étape 3, jamais écrit par l'admin.
 */
export const STATUTS_SELECTION = [
  PROSPECT_STATUS.A_APPELER,
  PROSPECT_STATUS.RAPPELER,
  PROSPECT_STATUS.QUALIFIE
];

/**
 * Statuts depuis lesquels une **demande de création assistée** peut être enregistrée (décision A6).
 * Seuls les deux états « le client a dit oui » : on ne crée pas de compte depuis `a_appeler`,
 * `perdu` ou `exclu` — la callable refuse aussi (`failed-precondition`), l'UI n'est qu'un confort.
 */
export const STATUTS_CREATION_DEMANDE = [PROSPECT_STATUS.QUALIFIE, PROSPECT_STATUS.RDV];

/** Alias lisible de `PROSPECT_STATUS.DEMANDE_CREEE`, écrit par la callable `createProspectAccount`. */
export const DEMANDE_CREEE = PROSPECT_STATUS.DEMANDE_CREEE;

/**
 * Vrai si le bouton « ➕ Enregistrer la demande » doit être **actif** (A6 + A5).
 *
 * Fonction pure, testable sans Firestore. Deux verrous, pas un :
 *  - le statut doit être `qualifie` ou `rdv` (A6) ;
 *  - `userId` doit être vide (A5) : une fiche déjà reliée à un compte ne peut pas en créer un
 *    second, et `optedOut` ferme la porte même si le statut a été forcé.
 *
 * @param {object|null} prospect fiche formatée par `formaterProspect`
 * @returns {boolean}
 */
export const peutCreerDemande = (prospect) => {
  if (!prospect) return false;
  if (prospect.userId) return false;
  if (prospect.optedOut === true) return false;
  return STATUTS_CREATION_DEMANDE.includes(prospect.status);
};

export const MOTIFS_PERTE_LABELS = {
  refus: 'Refus',
  deja_site: 'A déjà un site',
  agence: 'Déjà accompagné',
  prix: 'Prix',
  injoignable: 'Injoignable',
  faux_numero: 'Faux numéro',
  ligne_hs: 'Ligne hors service',
  ferme: 'Entreprise fermée'
};

/* ─────────────────────── Les 15 issues d'appel (révisées le 30/10/2026) ─────────────────────── */

/** Exemple de saisie date/heure pour `<input type="datetime-local">`. */
export const CALL_OUTCOME_FAMILIES = [
  {
    id: 'A',
    titre: 'Pas de contact humain',
    issues: [
      'sans_reponse',
      'messagerie_message',
      'messagerie_pleine',
      'occupe',
      'standard',
      'faux_numero',
      'ligne_coupee'
    ]
  },
  {
    id: 'B',
    titre: 'Contact humain, pas de suite',
    issues: ['pas_interesse', 'a_deja_un_site', 'deja_accompagne', 'trop_cher', 'entreprise_fermee']
  },
  {
    id: 'C',
    titre: 'Contact humain, suite positive',
    issues: ['rappeler', 'interesse']
  },
  {
    id: 'D',
    titre: 'Sortie définitive',
    issues: ['opposition']
  }
];

/**
 * La table qui pilote tout : statut proposé, délai de rappel, champs conditionnels.
 *
 * 🔒 **Aucune entrée ne produit `converti`** (test verrou n°8). Si quelqu'un en ajoute une, le
 * test casse immédiatement : un bouton de l'admin ne peut pas fabriquer une conversion payée.
 */
export const CALL_OUTCOME_EFFECTS = {
  sans_reponse: { status: PROSPECT_STATUS.A_APPELER, delayHours: 48, attempt: true },
  messagerie_message: { status: PROSPECT_STATUS.A_APPELER, delayHours: 72, attempt: true },
  messagerie_pleine: {
    status: PROSPECT_STATUS.A_APPELER,
    delayHours: 24,
    attempt: true,
    note: 'autre créneau'
  },
  occupe: { status: PROSPECT_STATUS.A_APPELER, delayHours: 2, attempt: true },
  standard: { status: PROSPECT_STATUS.A_APPELER, delayHours: 24, attempt: true },
  faux_numero: { status: PROSPECT_STATUS.EXCLU, lostReason: 'faux_numero', attempt: false },
  ligne_coupee: { status: PROSPECT_STATUS.EXCLU, lostReason: 'ligne_hs', attempt: false },
  pas_interesse: { status: PROSPECT_STATUS.PERDU, lostReason: 'refus' },
  a_deja_un_site: { status: PROSPECT_STATUS.PERDU, lostReason: 'deja_site' },
  deja_accompagne: { status: PROSPECT_STATUS.PERDU, lostReason: 'agence' },
  trop_cher: { status: PROSPECT_STATUS.PERDU, lostReason: 'prix' },
  entreprise_fermee: { status: PROSPECT_STATUS.EXCLU, lostReason: 'ferme' },
  rappeler: { status: PROSPECT_STATUS.RAPPELER, needsDate: true },
  interesse: { status: PROSPECT_STATUS.QUALIFIE, delayHours: 48 },
  opposition: { status: PROSPECT_STATUS.OPPOSITION, terminal: true }
};

export const CALL_OUTCOME_LABELS = {
  sans_reponse: 'Sonne dans le vide',
  messagerie_message: 'Messagerie — message laissé',
  messagerie_pleine: 'Messagerie pleine / muette',
  occupe: 'Occupé',
  standard: 'Standard / accueil',
  faux_numero: 'Pas la bonne entreprise',
  ligne_coupee: 'Ligne hors service',
  pas_interesse: 'Pas intéressé',
  a_deja_un_site: 'A déjà un site',
  deja_accompagne: 'Déjà accompagné',
  trop_cher: 'Trop cher',
  entreprise_fermee: 'Entreprise fermée',
  rappeler: 'À rappeler',
  interesse: 'Intéressé',
  opposition: '⛔ Ne plus appeler'
};

/** « Quand l'utiliser » — affiché en info-bulle sur chaque bouton. */
export const CALL_OUTCOME_DESCRIPTIONS = {
  sans_reponse: '≥ 8 sonneries, personne ne décroche',
  messagerie_message: 'Répondeur : l’annonce se lance et on enregistre un message',
  messagerie_pleine: 'Boîte non configurée, saturée, ou aucun message possible',
  occupe: 'Tonalité « occupé »',
  standard: 'Un tiers filtre l’appel, l’artisan n’est jamais en ligne',
  faux_numero: 'Un particulier ou un autre commerce répond',
  ligne_coupee: 'Numéro non attribué, tonalité d’erreur, « n’est plus en service »',
  pas_interesse: 'Refus net, sans motif précis',
  a_deja_un_site: 'Et il en est satisfait',
  deja_accompagne: 'Agence web / prestataire déjà en place',
  trop_cher: 'Le prix est évoqué et bloque',
  entreprise_fermee: 'Cessation, retraite, rachat, décès',
  rappeler: 'Il demande un rappel (date/heure à saisir)',
  interesse: 'Intéressé, rien de précis encore',
  opposition: 'Le contact demande à ne plus être sollicité (motif à saisir)'
};

export const CALL_OUTCOME_KEYS = Object.keys(CALL_OUTCOME_EFFECTS);

/** Vrai si la fiche accepte encore une issue d'appel. */
export const isCallable = (prospect) => {
  if (!prospect) return false;
  if (prospect.optedOut === true) return false;
  return !TERMINAL_STATUSES.includes(prospect.status);
};

export const isTerminal = (status) => TERMINAL_STATUSES.includes(status);

/* ─────────────────────────────── Fonctions pures du journal d'appel ─────────────────────────────── */

/** 3 tentatives famille A sans contact humain → retiré de la file (anti-harcèlement). */
export const MAX_TENTATIVES = 3;

/**
 * Date du prochain rappel, selon la table :
 * `delayHours` → maintenant + délai ; `needsDate` → la date choisie.
 */
export const computeNextCallAt = (effet, { nextCallAt = null, maintenant = new Date() } = {}) => {
  if (!effet) return null;
  if (effet.needsDate) return nextCallAt || null;
  if (typeof effet.delayHours === 'number') {
    return new Date(maintenant.getTime() + effet.delayHours * 60 * 60 * 1000);
  }
  return null;
};

/**
 * Patch **pur** écrit par un appel : aucun accès réseau, donc testable en deux lignes.
 *
 * Refus explicites :
 *  - issue inconnue → on ne devine pas ;
 *  - prospect `converti` → on ne dégrade jamais une conversion (test 7) ;
 *  - champ conditionnel manquant → `rappeler` sans date est une saisie incomplète.
 *
 * @param {string} outcome l'une des 15 issues
 * @param {{note?: string|null, nextCallAt?: Date|null, motif?: string|null,
 *          maintenant?: Date, prospect?: object}} options
 */
export const buildCallPatch = (outcome, options = {}) => {
  const {
    note = null,
    nextCallAt = null,
    motif = null,
    maintenant = new Date(),
    prospect = {}
  } = options;

  const effet = CALL_OUTCOME_EFFECTS[outcome];
  if (!effet) {
    throw new Error(`prospectService: issue d'appel inconnue « ${outcome} »`);
  }

  if (prospect && prospect.status === PROSPECT_STATUS.CONVERTI) {
    throw new Error('prospectService: une conversion ne se dégrade pas — aucun appel possible');
  }

  if (effet.needsDate && !nextCallAt) {
    throw new Error('prospectService: l’issue « rappeler » exige une date de rappel');
  }

  const prochainAppel = computeNextCallAt(effet, { nextCallAt, maintenant });
  const tentatives = Number(prospect?.callAttempts) || 0;
  const tentativesEpuisees = effet.attempt === true && tentatives + 1 >= MAX_TENTATIVES;

  const patch = {
    status: tentativesEpuisees ? PROSPECT_STATUS.PERDU : effet.status,
    lostReason: tentativesEpuisees ? 'injoignable' : effet.lostReason || null,
    nextCallAt: tentativesEpuisees ? null : prochainAppel,
    callAttemptsIncrement: effet.attempt === true ? 1 : 0,
    tentativesEpuisees,
    note,
    motif,
    optedOut: outcome === 'opposition' ? true : Boolean(prospect?.optedOut),
    optedOutAt: outcome === 'opposition' ? maintenant : prospect?.optedOutAt || null,
    milestonesFields: {}
  };

  // Jalons : posés une seule fois, au moment où l'événement se produit
  if (!prospect?.milestones?.firstCallAt) {
    patch.milestonesFields['milestones.firstCallAt'] = maintenant;
  }
  if (
    patch.status === PROSPECT_STATUS.QUALIFIE &&
    !prospect?.milestones?.qualifiedAt
  ) {
    patch.milestonesFields['milestones.qualifiedAt'] = maintenant;
  }

  return patch;
};

/** Entrée du journal `calls[]` — append-only, jamais modifiée ni tronquée. */
export const buildCallEntry = (outcome, { note = null, nextCallAt = null, uid = null, maintenant = new Date() } = {}) => ({
  at: maintenant,
  outcome,
  note,
  by: uid,
  nextCallAt
});

/* ─────────────────────────────── Accès Firestore ─────────────────────────────── */

const prospectsRef = () => collection(firestore, PROSPECTS_KEY);
const prospectRef = (placeId) => doc(firestore, PROSPECTS_KEY, placeId);

/** `Date` → `Timestamp`, récursivement. Les sentinelles Firestore passent inchangées. */
export const versFirestore = (valeur) => {
  if (valeur instanceof Date) return Timestamp.fromDate(valeur);
  if (Array.isArray(valeur)) return valeur.map(versFirestore);
  if (valeur && typeof valeur === 'object' && Object.getPrototypeOf(valeur) === Object.prototype) {
    return Object.entries(valeur).reduce((acc, [cle, element]) => {
      acc[cle] = versFirestore(element);
      return acc;
    }, {});
  }
  return valeur; // FieldValue, Timestamp, primitives
};

/** `Timestamp` → `Date`, récursivement : l'UI ne manipule jamais de `Timestamp`. */
export const depuisFirestore = (valeur) => {
  if (valeur && typeof valeur.toDate === 'function') return valeur.toDate();
  if (Array.isArray(valeur)) return valeur.map(depuisFirestore);
  if (valeur && typeof valeur === 'object' && Object.getPrototypeOf(valeur) === Object.prototype) {
    return Object.entries(valeur).reduce((acc, [cle, element]) => {
      acc[cle] = depuisFirestore(element);
      return acc;
    }, {});
  }
  return valeur;
};

/** Document Firestore → objet d'UI (dates en `Date`, `placeId` toujours présente). */
export const formaterProspect = (placeId, data = {}) => ({
  ...depuisFirestore(data),
  placeId: placeId || data.placeId || null
});

/**
 * Écrit les documents normalisés par `analyserImport` en lots de 400 (`writeBatch` : la limite
 * Firestore est 500 opérations, 400 laisse de la marge).
 *
 * `setDoc(…, { merge: true })` sur l'ID `placeId` : **l'import est idempotent**, réimporter ne
 * crée aucun doublon.
 *
 * ⚠️ Deux protections indispensables, parce que `merge` est **superficiel** :
 *  - les champs CRM vides (`calls: []`, `callNotes: ''`, `callAttempts: 0`, `optedOut: false`,
 *    dates `null`) ne sont **pas écrits** : sinon un réimport effacerait le journal d'appel, qui
 *    est la piste d'audit RGPD et ne doit jamais rétrécir ;
 *  - `milestones` est écrit en **notation point** (`milestones.importedAt`) : les 4 jalons aval
 *    survivent au réimport.
 */
export const allegerPourFusion = (document) => {
  const copie = { ...document };
  const milestones = copie.milestones || {};
  delete copie.milestones;

  if (!Array.isArray(copie.calls) || copie.calls.length === 0) delete copie.calls;
  if (!copie.callNotes) delete copie.callNotes;
  if (!copie.callAttempts) delete copie.callAttempts;
  if (!copie.lastCallAt) delete copie.lastCallAt;
  if (!copie.nextCallAt) delete copie.nextCallAt;
  if (!copie.lostReason) delete copie.lostReason;
  if (!copie.email) delete copie.email;
  if (copie.optedOut !== true) {
    delete copie.optedOut;
    delete copie.optedOutAt;
  }

  if (milestones.importedAt) {
    copie['milestones.importedAt'] = milestones.importedAt;
  }

  return copie;
};

export const importProspects = async (documents = [], { uid = null, resume = null } = {}) => {
  const liste = Array.isArray(documents) ? documents : [];
  const lots = [];

  for (let index = 0; index < liste.length; index += 400) {
    lots.push(liste.slice(index, index + 400));
  }

  let ecrits = 0;

  for (const lot of lots) {
    const batch = writeBatch(firestore);
    lot.forEach((document) => {
      const { placeId, ...donnees } = document;
      const fusion = allegerPourFusion({ ...donnees, placeId });
      batch.set(doc(firestore, PROSPECTS_KEY, placeId), versFirestore(fusion), { merge: true });
    });
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
    ecrits += lot.length;
  }

  // Journal : qui, quand, quel fichier, combien (§5.3 point 6)
  let journalId = null;
  try {
    const journalRef = doc(firestore, PROSPECT_IMPORTS_KEY, `import_${Date.now()}`);
    await setDoc(journalRef, {
      ...versFirestore(resume || {}),
      importedAt: serverTimestamp(),
      importedBy: uid
    });
    journalId = journalRef.id;
  } catch (error) {
    // Le journal ne doit jamais faire échouer un import réussi
    console.error('prospectService: journal d’import non écrit', error);
  }

  return { ecrits, journalId };
};

/**
 * Création d'une **fiche saisie à la main** (§5.2 bis) : `setDoc` **sans `merge`**, parce que le
 * document sort complet de `construireProspectManuel` (même schéma que l'import).
 *
 * Pas de `getDoc` préalable ici : le dédoublonnage se fait **par téléphone normalisé** dans l'UI
 * (`trouverDoublonTelephone`), car le `placeId` manuel (`manuel-<horodatage>`) est unique par
 * construction et ne peut donc pas révéler un doublon.
 *
 * @returns {Promise<string>} le `placeId` créé
 */
export const createProspect = async (document = {}, { uid = null } = {}) => {
  const placeId = document?.placeId;
  if (!placeId) {
    throw new Error('prospectService: impossible de créer une fiche sans `placeId`');
  }

  await setDoc(
    prospectRef(placeId),
    versFirestore({ ...document, importedBy: uid, updatedBy: uid })
  );

  return placeId;
};

/** Flux temps réel de la collection, trié côté client par score décroissant. */
export const observeProspects = (onNext, onError) =>
  onSnapshot(
    prospectsRef(),
    (snapshot) => {
      const prospects = snapshot.docs
        .map((document) => formaterProspect(document.id, document.data()))
        .sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
      onNext(prospects);
    },
    onError
  );

/** Flux temps réel d'une fiche (route `prospection/:placeId`). */
export const observeProspect = (placeId, onNext, onError) =>
  onSnapshot(
    prospectRef(placeId),
    (snapshot) => {
      if (!snapshot.exists()) {
        onNext(null);
        return;
      }
      onNext(formaterProspect(snapshot.id, snapshot.data()));
    },
    onError
  );

/**
 * Enregistre une issue d'appel : `calls[]` (append-only), `lastCallAt`, `status`, jalons.
 * C'est le **seul** chemin d'écriture d'un statut terminal à l'étape 1.
 */
export const recordCall = async (placeId, outcome, options = {}) => {
  const { uid = null, prospect = {}, note = null, motif = null } = options;
  const patch = buildCallPatch(outcome, { ...options, prospect });

  const operations = {
    status: patch.status,
    lostReason: patch.lostReason,
    nextCallAt: patch.nextCallAt,
    optedOut: patch.optedOut,
    optedOutAt: patch.optedOutAt,
    calls: arrayUnion(
      versFirestore(buildCallEntry(outcome, { note, nextCallAt: patch.nextCallAt, uid }))
    ),
    lastCallAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
    ...patch.milestonesFields
  };

  if (note) operations.callNotes = note;
  if (motif) operations.oppositionMotif = motif;
  if (patch.callAttemptsIncrement > 0) {
    operations.callAttempts = increment(patch.callAttemptsIncrement);
  }

  await updateDoc(prospectRef(placeId), versFirestore(operations));

  return patch;
};

/**
 * Transition manuelle. Refusée si la cible est un statut terminal ou `converti` :
 * `exclu` et `perdu` se quittent depuis la fiche, ils ne s'y entrent pas à la main.
 */
export const updateProspectStatus = async (placeId, status, { uid = null } = {}) => {
  if (status === PROSPECT_STATUS.CONVERTI) {
    throw new Error('prospectService: `converti` ne peut être écrit que par le trigger Stripe');
  }
  if (isTerminal(status)) {
    throw new Error(
      `prospectService: le statut « ${status} » s’obtient par une issue d’appel, pas à la main`
    );
  }

  await updateDoc(prospectRef(placeId), {
    status,
    updatedAt: serverTimestamp(),
    updatedBy: uid
  });
};

/** Réouverture d'une fiche `perdu` / `exclu` (réversible, avec confirmation côté UI). */
export const rouvrirProspect = async (placeId, { uid = null } = {}) => {
  await updateDoc(prospectRef(placeId), {
    status: PROSPECT_STATUS.A_APPELER,
    lostReason: null,
    nextCallAt: null,
    callAttempts: 0,
    updatedAt: serverTimestamp(),
    updatedBy: uid
  });
};

/**
 * Champs éditables sur la fiche : adresse, email, `activitySummary`, `services`.
 * Dès que l'opérateur touche aux services, `servicesModifiesPar` est posé et le réimport
 * n'écrase plus ni les services ni le résumé (§3.2 merge).
 */
export const updateProspectFields = async (placeId, patch = {}, { uid = null } = {}) => {
  const operations = { ...patch, updatedAt: serverTimestamp(), updatedBy: uid };

  if (Object.prototype.hasOwnProperty.call(patch, 'services')) {
    operations.servicesModifiesPar = uid;
  }

  await updateDoc(prospectRef(placeId), versFirestore(operations));
};

/** Recalcule `score` + `scoreDetail` de tous les documents, sans réimport (barème qui évolue). */
export const recalculerScores = async () => {
  const snapshot = await getDocs(prospectsRef());
  let misAJour = 0;

  for (const document of snapshot.docs) {
    const donnees = formaterProspect(document.id, document.data());
    const { score, scoreDetail } = scoreProspect(donnees);

    if (score !== donnees.score) {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(document.ref, { score, scoreDetail: versFirestore(scoreDetail) });
      misAJour += 1;
    }
  }

  return { total: snapshot.size, misAJour };
};

/* ─────────────────────── Étape 2 — la demande assistée (§2 et §3) ─────────────────────── */

/**
 * L'adresse a-t-elle **déjà** un compte ? (garde A4, exposée à l'UI)
 *
 * Le client admin ne peut pas interroger Firebase Auth : cette callable répond
 * `{ exists, uid?, lastSignIn?, hasCreationRequest? }`. `hasCreationRequest` et `lastSignIn` ne sont
 * pas décoratifs — ils permettent à l'agent de dire « vous avez déjà un site chez nous, je vous
 * passe le support » au lieu de bloquer sèchement.
 *
 * ⚠️ L'UI n'est **pas** une garantie : `createProspectAccount` revérifie côté serveur (A4 durci).
 *
 * @param {string} email adresse saisie pendant l'appel
 * @returns {Promise<{exists: boolean, uid?: string, lastSignIn?: string|null,
 *                    hasCreationRequest?: boolean, invalid?: boolean}>}
 */
export const checkAccountEmail = async (email) => {
  const fn = httpsCallable(functions, 'checkAccountEmail');
  const { data } = await fn({ email: String(email || '').trim().toLowerCase() });
  return data || { exists: false };
};

/**
 * Crée le **compte réel** + la demande assistée depuis une fiche prospect (étape 2).
 *
 * Ce que fait le serveur, dans un ordre non négociable : 7 gardes, 1 `createUser` (sans mot de
 * passe), 5 écritures Firestore en un seul lot, et un rollback `auth.deleteUser` si le lot échoue.
 * **La callable n'envoie aucun mail** : le seul mail part du trigger `onAccountCreated`, déclenché
 * par l'écriture de `users/{uid}`. Un double clic renvoie `{ alreadyCreated: true }` sans écrire.
 *
 * @param {{placeId: string, email: string, form: object}} payload
 * @returns {Promise<{userId?: string, email?: string, activationUrl?: string,
 *                    alreadyCreated: boolean, creationRequestId?: string}>}
 */
export const createProspectAccount = async ({ placeId, email, form } = {}) => {
  if (!placeId) {
    throw new Error('prospectService: `placeId` requis pour créer une demande');
  }

  const fn = httpsCallable(functions, 'createProspectAccount');
  const { data } = await fn({
    placeId,
    email: String(email || '').trim().toLowerCase(),
    form
  });

  return data;
};
